import useInterval from 'beautiful-react-hooks/useInterval';
import { I18nNamespace } from 'constants/i18n.const';
import {
  SECOND_VAL,
  TIME_DEBOUNCE_TAKE_PHOTO,
  TIME_TO_CAMERA_FLASH,
} from 'constants/photo.const';
import { useShootingContext } from 'containers/selfe-booth/shooting/ShootingContext';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { emitSeqLog } from 'functions/seq-logging.func';
import { secToMil } from 'helpers/time.helper';
import { useModal } from 'hooks/useModal';
import { useRobotArmHandler } from 'hooks/useRobotArmHandler';
import { useTimer } from 'hooks/useTimer';
import { useTranslation } from 'hooks/useTranslation';
import { size, slice } from 'lodash';
import { useCallback, useRef } from 'react';
import {
  camerasApi,
  useRecordVideoApiActionMutation,
  useRecordVideoOriginApiActionMutation,
  useStartRecordVideoApiActionMutation,
  useTakePhotoApiActionMutation,
  useTakePhotoOriginApiActionMutation,
} from 'store/api/camera.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useShootingActions } from './useShootingActions';

export const useShootingHandlers = () => {
  const { t } = useTranslation([
    I18nNamespace.COMMON,
    I18nNamespace.PAGE,
    I18nNamespace.WARNING,
  ]);
  const firstStartRef = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const { listPhotoTakens } = useBoothAppContext();
  const { openConfirmModal, closeConfirmModal } = useModal();
  const [takePhotoAction] = useTakePhotoApiActionMutation();
  const [takePhotoOriginAction] = useTakePhotoOriginApiActionMutation();
  const [startRecordVideoAction] = useStartRecordVideoApiActionMutation();
  const [recordVideoAction] = useRecordVideoApiActionMutation();
  const [recordVideoOriginAction] = useRecordVideoOriginApiActionMutation();
  const { audio } = useSoundContext();
  const { handleMoveRobotArm } = useRobotArmHandler();

  const {
    transactionId,
    wasRetakePhoto,
    isRetakePhoto, // trong giai đoạn chụp lại
    isShootingOfRetakingPhoto, // trong giai đoạn chụp lại, và đang chụp ảnh
  } = useAppSelector((state) => state.selfeApp);
  const { isTakePhotoOrigin } = useAppSelector((s) => s.booth);
  const {
    currentCameraId,
    isCameraSessionOpen,
    isCountDownMode,
    numberOfTakePicture,
    sizeOfNeedToTakePhoto,
    countDownTakePictureSecond,
    delayCountDownTakePictureSecond,
    timeLimitTakePictureSecond,

    countDownSec,
    isStartCountDownShooting,
    isWaitingApiShooting,
    isDelayShooting,
    listIndexOfRetake,

    setCountDownSec,
    setIsStartCountDownShooting,
    setIsWaitingApiShooting,
    setIsFlashCamera,
    setIsDelayShooting,
    setListIndexOfRetake,
    setImageHash,
  } = useShootingContext();

  const { handleRedirectNext } = useShootingActions();

  // continue button auto click
  const {
    second: delayTimeCountDownSeconds,
    resetTime: resetDelayTimeCountDown,
  } = useTimer({
    isEnabled:
      isCameraSessionOpen &&
      isCountDownMode &&
      delayCountDownTakePictureSecond > 0,
    totalMilliseconds: secToMil(delayCountDownTakePictureSecond),
    timeUpFunc: () => setIsDelayShooting(false),
  });

  const {
    second: timeLimitSeconds,
    resetTime: resetTimeLimit,
    stopTime: stopTimeLimit,
  } = useTimer({
    isEnabled:
      isCameraSessionOpen &&
      !isCountDownMode &&
      isStartCountDownShooting &&
      timeLimitTakePictureSecond > 0,
    totalMilliseconds: secToMil(timeLimitTakePictureSecond),
    timeUpFunc: () => handleTakePhoto?.(),
  });

  // Sau khi goi api chụp ảnh
  const handleAfterTakeAPhoto = useCallback(() => {
    if (isRetakePhoto) setListIndexOfRetake((l: any) => slice(l, 1));
    setTimeout(() => setImageHash(Date.now()), TIME_TO_CAMERA_FLASH);
  }, [isRetakePhoto, setImageHash, setListIndexOfRetake]);

  const handleStartRecordVideo = useCallback(() => {
    startRecordVideoAction({ id: currentCameraId });
  }, [currentCameraId, startRecordVideoAction]);

  // xử lý bắt đầu chụp ảnh count down
  const handleStartPhoto = useCallback(() => {
    emitSeqLog?.({
      messageTemplate: `[Shooting] Start shooting with vid (handleStartPhoto)`,
    });
    if (!firstStartRef.current) {
      // eslint-disable-next-line no-param-reassign
      firstStartRef.current = true;
      handleStartRecordVideo();
      setIsStartCountDownShooting(true);
    }
  }, [firstStartRef, handleStartRecordVideo, setIsStartCountDownShooting]);

  // Chụp bằng remote
  const handleDelayNextShooting = () => {
    if (delayCountDownTakePictureSecond > 0) {
      setIsDelayShooting(true);
      resetDelayTimeCountDown();
    }
  };

  // Confirm chụp lại ảnh
  const handleContinueOrConfirmReTakePhoto = () => {
    if (wasRetakePhoto || !size(listIndexOfRetake)) {
      handleRedirectNext(!wasRetakePhoto);
    } else {
      audio?.playRetakePhoto?.();
      openConfirmModal({
        content: `${t('page:retakeAPhotoConfirm')}`,
        onOk: async () => {
          closeConfirmModal?.();
          await handleMoveRobotArm(listIndexOfRetake?.[0]);
          dispatch(setSelfeAppStateAction({ isShootingOfRetakingPhoto: true }));
          if (isCountDownMode) {
            setCountDownSec(countDownTakePictureSecond);
            handleStartRecordVideo();
          } else {
            resetTimeLimit();
          }
        },
      });
    }
  };

  // Xử lý event trả về từ máy ảnh nếu chụp ảnh lỗi
  const handleListenTakePhotoFailed = useDebouncedCallback(
    useCallback(async () => {
      openConfirmModal({
        content: `${t('page:warningCanonCantFocus')}`,
        hidePrev: true,
        onOk: async () => {
          closeConfirmModal?.();
          if (isCountDownMode) {
            if (sizeOfNeedToTakePhoto + 1 <= numberOfTakePicture) {
              await handleStartRecordVideo();
              setCountDownSec(countDownTakePictureSecond);
              handleDelayNextShooting();
            }
          } else {
            resetTimeLimit();
          }
          setIsWaitingApiShooting(false);
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCountDownMode, numberOfTakePicture, sizeOfNeedToTakePhoto]),
    TIME_DEBOUNCE_TAKE_PHOTO,
    { trailing: false, leading: true },
  );

  // Xử lý event trả về từ máy ảnh
  const handleListenTakePhoto = useDebouncedCallback(
    useCallback(async () => {
      if (isRetakePhoto && !isShootingOfRetakingPhoto) return;
      if (!transactionId) return;
      // lấy ảnh mới
      dispatch(camerasApi.util.invalidateTags([{ type: 'PhotoTaken' }]));
      // di chuyển cánh tay đến vị trí mới
      await handleMoveRobotArm(
        (isRetakePhoto ? listIndexOfRetake?.[0] : sizeOfNeedToTakePhoto) + 1,
      );

      if (isCountDownMode) {
        if (sizeOfNeedToTakePhoto + 1 < numberOfTakePicture) {
          await handleStartRecordVideo();
          setCountDownSec(countDownTakePictureSecond);
          handleDelayNextShooting();
        }
      } else {
        resetTimeLimit();
      }
      handleAfterTakeAPhoto();
      setIsWaitingApiShooting(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isRetakePhoto,
      isShootingOfRetakingPhoto,
      transactionId,
      dispatch,
      handleMoveRobotArm,
      listIndexOfRetake,
      sizeOfNeedToTakePhoto,
      isCountDownMode,
      handleAfterTakeAPhoto,
      numberOfTakePicture,
    ]),
    TIME_DEBOUNCE_TAKE_PHOTO,
    { trailing: false, leading: true },
  );

  // Chụp bằng remote
  const handleTakePhoto = useDebouncedCallback(
    useCallback(async () => {
      if (isRetakePhoto && !isShootingOfRetakingPhoto) return;
      if (
        !transactionId ||
        isCountDownMode ||
        isWaitingApiShooting ||
        !isStartCountDownShooting
      )
        return;

      setIsWaitingApiShooting(true);
      setIsFlashCamera(true);
      audio?.playCameraShutter();

      const takePhotoPayload = {
        id: currentCameraId,
        transactionid: transactionId,
        filename: isRetakePhoto
          ? listPhotoTakens?.data?.[listIndexOfRetake?.[0]] ?? ''
          : '',
      };
      stopTimeLimit();
      if (!isTakePhotoOrigin) {
        // chụp ảnh
        await takePhotoAction(takePhotoPayload);
        // di chuyển cánh tay đến vị trí mới
        await handleMoveRobotArm(
          (isRetakePhoto ? listIndexOfRetake?.[0] : sizeOfNeedToTakePhoto) + 1,
        );
        resetTimeLimit();
        handleAfterTakeAPhoto();
        setIsWaitingApiShooting(false);
      } else {
        await takePhotoOriginAction(takePhotoPayload);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      handleAfterTakeAPhoto,
      isTakePhotoOrigin,
      isCountDownMode,
      isWaitingApiShooting,
      isRetakePhoto,
      isShootingOfRetakingPhoto,
      listIndexOfRetake,
      listPhotoTakens?.data,
      takePhotoAction,
      transactionId,
      currentCameraId,
      isStartCountDownShooting,
    ]),
    TIME_DEBOUNCE_TAKE_PHOTO,
    { trailing: false, leading: true },
  );

  // Chụp tự động
  useInterval(async () => {
    if (!isCountDownMode) return; // không chạy ở chế độ remote
    if (!isStartCountDownShooting) return; // Chờ khi KH bấm bắt đầu hoặc
    if (isRetakePhoto && !isShootingOfRetakingPhoto) return;
    if (isWaitingApiShooting) return; // Chờ api take picture done
    if (isDelayShooting) return; // Delay giữa các lần chụp

    if (sizeOfNeedToTakePhoto < numberOfTakePicture) {
      setCountDownSec((sec: any) => (sec - 1) % countDownTakePictureSecond);
      countDownSec > 0 && audio?.playSecondTick();
    }
    if (transactionId && countDownSec === 0) {
      setIsWaitingApiShooting(true);
      setIsFlashCamera(true);
      audio?.playCameraShutter();

      const recordVideoPayload = {
        id: currentCameraId,
        transactionid: transactionId,
        filename: isRetakePhoto
          ? listPhotoTakens?.data?.[listIndexOfRetake?.[0]] ?? ''
          : '',
      };
      if (!isTakePhotoOrigin) {
        const res: any = await recordVideoAction(recordVideoPayload);
        if (sizeOfNeedToTakePhoto + 1 < numberOfTakePicture || !!res?.error) {
          await handleStartRecordVideo();
          await handleMoveRobotArm(
            (isRetakePhoto ? listIndexOfRetake?.[0] : sizeOfNeedToTakePhoto) +
              1,
          );
          setCountDownSec(countDownTakePictureSecond);
          handleDelayNextShooting();
        }
        handleAfterTakeAPhoto();
        setIsWaitingApiShooting(false);
      } else {
        await recordVideoOriginAction(recordVideoPayload);
      }
    }
  }, SECOND_VAL);

  return {
    handleTakePhoto,
    handleStartPhoto,
    handleDelayNextShooting,
    handleListenTakePhoto,
    handleListenTakePhotoFailed,
    handleStartRecordVideo,
    handleAfterTakeAPhoto,
    handleContinueOrConfirmReTakePhoto,
    delayTimeCountDownSeconds,
    timeLimitSeconds,
    stopTimeLimit,
  };
};
